import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';

interface AdminUser {
  username: string;
  email?: string;
}

// What signIn came back with. An invited/reset admin signs in with a temporary
// password and Cognito answers NEW_PASSWORD_REQUIRED instead of tokens; the
// login page then collects the new password (plus any profile attributes the
// pool requires that the account is missing - older accounts moved into the
// shared admin pool pre-date the invite flow that sets given/family name).
export type SignInResult =
  | { step: 'DONE' }
  | { step: 'NEW_PASSWORD_REQUIRED'; missingAttributes: string[] };

interface AuthContextValue {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeNewPassword: (newPassword: string, attributes: Record<string, string>) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Confirms there's a live session (not just a cached username) by requiring
// fetchAuthSession to resolve with tokens, matching the direct-SRP flow (no
// Hosted UI redirect) that racing-react uses.
async function loadCurrentUser(): Promise<AdminUser | null> {
  const current = await getCurrentUser();
  const session = await fetchAuthSession();
  if (!session.tokens) {
    return null;
  }
  return { username: current.username, email: current.signInDetails?.loginId };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadCurrentUser()
      .then((loadedUser) => {
        if (!cancelled) setUser(loadedUser);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignIn = async (email: string, password: string): Promise<SignInResult> => {
    const { nextStep } = await amplifySignIn({ username: email, password });
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { step: 'NEW_PASSWORD_REQUIRED', missingAttributes: nextStep.missingAttributes ?? [] };
    }
    setUser(await loadCurrentUser());
    return { step: 'DONE' };
  };

  const handleCompleteNewPassword = async (newPassword: string, attributes: Record<string, string>) => {
    await amplifyConfirmSignIn({ challengeResponse: newPassword, options: { userAttributes: attributes } });
    setUser(await loadCurrentUser());
  };

  const handleSignOut = async () => {
    await amplifySignOut();
    setUser(null);
  };

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    signIn: handleSignIn,
    completeNewPassword: handleCompleteNewPassword,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
